/**
 * SsoCallbackScreen — rota /auth/callback, retorno do SSO Microsoft Entra ID.
 *
 * O backend já fez todo o trabalho (troca do code, validação do id_token,
 * provisionamento) e nos devolve os tokens do Flow no fragmento da URL. Aqui só
 * aplicamos a sessão e seguimos para o destino original.
 *
 * A tela é intencionalmente mínima: é um ponto de passagem de fração de segundo.
 * Se não houver tokens (acesso direto à rota, ou fragmento já consumido por um
 * duplo-render), voltamos ao /login em vez de deixar o usuário travado.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C } from '@/theme/palette';
import { useAuth } from '@/auth/AuthContext';
import { consumeCallbackTokens } from '@/auth/ssoClient';

export function SsoCallbackScreen() {
  const { applySsoTokens } = useAuth();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  // O fragmento só pode ser consumido uma vez (a URL é limpa em seguida); o ref
  // impede que o StrictMode do React, que monta o efeito duas vezes em dev,
  // rode o consumo de novo e conclua que a sessão falhou.
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    consumedRef.current = true;

    const tokens = consumeCallbackTokens();
    if (!tokens) {
      setFailed(true);
      navigate('/login?error=sso_failed', { replace: true });
      return;
    }

    applySsoTokens(tokens);
    navigate(tokens.next || '/dashboard', { replace: true });
  }, [applySsoTokens, navigate]);

  return (
    <div
      role="status"
      aria-label="Concluindo login..."
      style={{
        minHeight: '100vh',
        backgroundColor: C.black,
        color: C.neutral,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: `3px solid ${C.blackBorder}`,
          borderTopColor: C.cyber,
          animation: 'nava-spin 0.8s linear infinite',
        }}
      />
      <p style={{ fontSize: 14, color: C.neutralDim ?? C.neutral }}>
        {failed ? 'Redirecionando...' : 'Concluindo seu login...'}
      </p>
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes nava-spin { to { transform: rotate(360deg); } }
        }
      `}</style>
    </div>
  );
}

export default SsoCallbackScreen;
